import React, { useEffect, useRef } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReviewCard from './ReviewCard';
import ReviewsSummary from './ReviewsSummary';
import { capitalizeName } from '../../../shared/utils';
import { UserReview } from '../../types/review';

interface AllReviewsModalProps {
    open: boolean;
    onClose: () => void;
    reviews: UserReview[];
    averageRatings: {
        overall: number;
        access: number;
        accuracy: number;
        cleanliness: number;
        communication: number;
        value: number;
    };
    selectedReviewId?: number;
    highlightedReviewIds?: string[];
    highlightKeyword?: string;
    highlightSentiment?: 'positive' | 'negative';
    highlightPositiveCount?: number;
    highlightNegativeCount?: number;
}

const AllReviewsModal: React.FC<AllReviewsModalProps> = ({
    open,
    onClose,
    reviews,
    averageRatings,
    selectedReviewId,
    highlightedReviewIds = [],
    highlightKeyword,
    highlightSentiment,
    highlightPositiveCount,
    highlightNegativeCount,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const reviewRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    useEffect(() => {
        if (open && selectedReviewId) {
            setTimeout(() => {
                const element = reviewRefs.current[selectedReviewId];
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            }, 300);
        }
    }, [open, selectedReviewId]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={isMobile}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: isMobile ? 0 : '12px',
                    maxHeight: '90vh',
                    maxWidth: '920px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    justifyContent: 'flex-end',
                    pb: 2,
                }}
            >
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={4.3}>
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                pr: { xs: 0, lg: 2 },
                            }}
                        >
                            <ReviewsSummary
                                variant='modal'
                                reviews={reviews}
                                averageRatings={averageRatings}
                            />

                            {highlightKeyword &&
                                highlightedReviewIds.length > 0 && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,

                                            border: `1px solid ${
                                                highlightSentiment ===
                                                'positive'
                                                    ? theme.palette.primary.main
                                                    : theme.palette.error.main
                                            }`,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography
                                            variant='subtitle2'
                                            sx={{
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                mb: 1,
                                            }}
                                        >
                                            Filtering by: "
                                            {capitalizeName(highlightKeyword)}"
                                        </Typography>
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                fontSize: '12px',
                                            }}
                                        >
                                            {highlightPositiveCount !==
                                                undefined &&
                                            highlightNegativeCount !==
                                                undefined &&
                                            highlightPositiveCount > 0 &&
                                            highlightNegativeCount > 0 ? (
                                                <>
                                                    Showing{' '}
                                                    {highlightPositiveCount}{' '}
                                                    positive and{' '}
                                                    {highlightNegativeCount}{' '}
                                                    negative mentions
                                                </>
                                            ) : (
                                                <>
                                                    Showing{' '}
                                                    {
                                                        highlightedReviewIds.length
                                                    }{' '}
                                                    review
                                                    {highlightedReviewIds.length !==
                                                    1
                                                        ? 's'
                                                        : ''}{' '}
                                                    with {highlightSentiment}{' '}
                                                    mentions
                                                </>
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={7.7}>
                        <Box display='flex' flexDirection='column' gap={2}>
                            {(highlightedReviewIds.length > 0
                                ? reviews.filter((review) =>
                                      highlightedReviewIds.includes(
                                          review.id.toString(),
                                      ),
                                  )
                                : reviews
                            ).map((review) => {
                                const isHighlighted =
                                    highlightedReviewIds.includes(
                                        review.id.toString(),
                                    );

                                return (
                                    <Box
                                        key={review.id}
                                        ref={(el: HTMLDivElement | null) => {
                                            reviewRefs.current[review.id] = el;
                                        }}
                                    >
                                        <ReviewCard
                                            review={review}
                                            isInModal={true}
                                            highlightKeyword={
                                                isHighlighted
                                                    ? highlightKeyword
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default AllReviewsModal;
